import type { ComponentType, ReactNode } from 'react';
import { Link } from 'react-router';
import {
  Activity,
  ArrowRight,
  Banknote,
  BriefcaseBusiness,
  Building2,
  Check,
  ClipboardCheck,
  Clock,
  Compass,
  Cpu,
  FileSearch,
  Gem,
  Globe,
  HeartHandshake,
  Hourglass,
  Layers,
  Mail,
  MapPin,
  MessagesSquare,
  PieChart,
  Rocket,
  ScanSearch,
  ShieldCheck,
  Sparkles,
  Target,
  Telescope,
  Timer,
  TrendingUp,
  UserSearch,
  Users,
  Workflow,
  Zap,
} from 'lucide-react';
import { ServiceSlug, type SettingValues } from '@tbbt/shared';
import {
  ButtonLink,
  Container,
  Eyebrow,
  Glow,
  Section,
  SectionHeading,
  sectionRhythm,
} from '@/components/ui';
import { useSite } from '@/app/site-context';
import { mediaUrl } from '@/lib/media';
import { externalHref } from '@/lib/external-url';
import { useReveal } from '@/lib/useReveal';
import HeroImage from './HeroImage';
import { RotatingArtwork } from './RotatingArtwork';
import { useCountUp } from '@/lib/useCountUp';
import { cn } from '@/lib/cn';

import { ContactBlock } from '@/components/marketing/ContactBlock';

/**
 * The marketing sections, section-for-section from the reference design.
 *
 * `docs/design/stitch-reference.html` is the source of truth for every measurement in
 * this file — section padding, container gutters, card radii, type sizes, gradients,
 * the orbit geometry. Where a literal appears here it was transcribed from that file
 * rather than chosen, so a reviewer can diff the two.
 *
 * The **copy is not** transcribed, and that is deliberate. The design was generated
 * around an automated-hiring product: its capability grid advertised "Automated
 * Sourcing", "Rapid Screening" and "Predictive Analytics", and it had sections
 * promising live pipeline dashboards. On the briefing call the client was explicit that
 * this is a services business with no product — the previous site's "automate 50% of
 * your hiring journey" was the specific line he asked to remove.
 *
 * So each block below keeps the design's structure and describes how the firm actually
 * delivers work. See docs/CONTENT-BRIEF.md for the section-by-section reconciliation.
 */

// -----------------------------------------------------------------------------
// Hero
// -----------------------------------------------------------------------------
/**
 * The About illustration's intrinsic size, so the browser reserves the right box before
 * it decodes and the section does not jump. The hero's own illustration is picked per
 * page load by `HeroImage` and carries its own intrinsic dimensions.
 *
 * Native, not downscaled. It renders at roughly 700 CSS pixels, so a 2x display asks for
 * about 1400 device pixels — an asset encoded at exactly 1400 has nothing spare, and this
 * one carries small captions ("identify the right talent" and the rest) that went to mush
 * at that size. 1672 gives  2.4x.
 */
const ABOUT_ILLUSTRATION = { width: 1672, height: 941 } as const;

/**
 * The two About illustrations, cross-fading on a timer — the same rotation the hero and
 * the contact banner run.
 *
 * `about-screen1` is resized to `screen.png`'s exact box, which is what lets both share
 * the `ABOUT_ILLUSTRATION` dimensions above and makes the swap invisible to layout.
 */
const ABOUT_IMAGE_NAMES = ['/screen', '/about-screen1'] as const;

export function Hero(): ReactNode {
  const { settings } = useSite();

  return (
    // `pt-32`, not the reference's `pt-40`: the nav pill is fixed and ends at 90px, so
    // 160px of padding left an 89px hole beneath it that read as a mistake rather than
    // as air. 128px sits the eyebrow about 56px under the pill.
    <section
      // The hero's bottom edge lands on the fold, which is what puts the divider there.
      //
      // Capped, though, and deliberately. The content is about 520px tall, so on a
      // viewport taller than roughly 820px "fill the screen" can only mean "add empty
      // space" — at 1080px that was a 430px hole. The cap trades the exact fold on tall
      // screens for a hero with nothing hollow in it; every common laptop height
      // (625–790px) still ends precisely on the fold.
      //
      // `svh` not `vh`: mobile browsers measure `vh` against the viewport with the
      // toolbar hidden, which overflows while it is showing.
      className="relative w-full overflow-hidden bg-surface pb-10 pt-32 min-h-[min(100svh,820px)]"
    >
      {/*
        Two decorative layers, in paint order: a drifting aurora wash, then a faint dot
        lattice for the light to register against. Both replace the reference design's
        pair of static blur circles. Neither is announced — they carry no information,
        and a screen reader reading "image, image" here would be pure noise.
      */}
      <div aria-hidden="true" className="aurora" />
      <div aria-hidden="true" className="dot-grid" />

      {/*
        `relative` without a `z-index`, deliberately.
        The decorative layers above are absolutely positioned, so this block still paints
        over them by document order — but `z-10` here would make it a stacking context,
        and the illustration inside could then no longer blend with the aurora behind it.
        The reference design had `z-10`; its blend was inert for the same reason.
      */}
      <Container
        pad="gutter"
        // The reference put `min-h-[70vh]` here with `items-center`, which is the wrong
        // element for it: slack got split above and below, so the eyebrow drifted away
        // from the nav as the viewport grew. The floor belongs on the section, where
        // spare height collects below the content instead of above it.
        className="relative"
      >
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <div className="flex flex-col items-start text-left">
            {/*
              The eyebrow, and the first place the hero stops being purple.

              A flat `bg-primary/10` pill above a purple headline above purple buttons was
              the fourth purple element in a column of four. It now runs teal into the
              brand purple with a ring to give it an edge, and carries a teal spark — a
              small warm-to-cool contrast at the top of the page, which is enough to make
              everything below it read as deliberate rather than monochrome.
            */}
            <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-accent-teal/15 to-primary/15 px-4 py-1.5 text-[11px] font-bold uppercase tracking-widest text-primary-strong ring-1 ring-inset ring-accent-teal/25">
              <Sparkles aria-hidden="true" className="size-3.5 text-accent-teal" />
              {settings.brand.tagline || 'Talent acquisition partner'}
            </span>

            {/*
            The design's headline was "Great companies are built on great people."
            Kept in spirit but made specific: the client's own explanation of the brand
            was about finding the rare combination a role actually needs, which is a
            sharper claim than a truism.
          */}
            {/*
            The whole headline is the gradient now, running near-black into the brand
            purple. Starting at `--color-heading` rather than at a mid purple keeps the
            first line at full contrast, so the effect is decorative rather than
            something the reader has to work through.
          */}
            <h1 className="text-gradient mb-5 bg-gradient-to-br from-heading via-primary-strong to-primary text-[44px] font-bold leading-[1.1] tracking-tight md:text-[56px]">
              The right person for the role, not just the available one.
            </h1>

            <p className="mb-7 max-w-xl text-[17px] leading-relaxed text-body">
              A recruitment boutique working across the UAE, India and beyond. We run executive
              search, permanent hiring, RPO and on-demand sourcing — and we tell you when a brief is
              asking for someone who does not exist.
            </p>

            <div className="flex w-full flex-col gap-4 sm:w-auto sm:flex-row">
              <ButtonLink to={settings.brand.primaryCtaHref || '/contact'} size="lg">
                {settings.brand.primaryCtaLabel || 'Hire talent'}
              </ButtonLink>
              {/*
              The design's second hero button was "Find a Job", which belongs to the
              careers surface the client deferred to Phase 2. Replaced with a link to
              the services the firm sells today.
            */}
              <ButtonLink
                to="/services"
                variant="secondary"
                size="lg"
                className="border-2 border-primary/20 bg-transparent text-primary hover:border-primary/40 hover:bg-transparent"
              >
                How we work
              </ButtonLink>
            </div>
          </div>

          <div className="flex h-full w-full items-center justify-center lg:justify-end">
            {/*
            `scale-110` and `mix-blend-multiply` are both the design's, and the blend has
            to sit on *this* element rather than on the image inside it. `scale-110` is a
            transform, and a transform forms a stacking context — an `img` blending inside
            one has nothing but its own transparent parent to blend against, so the rule is
            silently inert and the artwork's white ground paints over the glow behind it.
            Applied here, the element blends with the section, which is what makes the
            ground disappear. Every illustration in the rotation is drawn on flat white
            for exactly this reason.
          */}
            <div className="relative w-full max-w-[620px] origin-top scale-[1.24] mix-blend-multiply lg:max-w-[740px]">
              <HeroImage />
            </div>
          </div>
        </div>

        <HeroHighlights />
      </Container>
    </section>
  );
}

/**
 * The reassurance strip under the hero buttons.
 *
 * Three things a visitor weighing up a recruiter wants to know before scrolling:
 * where you are, how you work, and how quickly you answer. Every one is read from
 * settings rather than written here, so none of them can drift out of date against
 * the contact page — and each disappears rather than showing a placeholder if the
 * setting behind it is empty.
 */
function HeroHighlights(): ReactNode {
  const { settings } = useSite();

  const offices = settings.contact.offices.map((office) => office.label).filter(Boolean);

  /*
    Each highlight carries its own accent, rather than three identical lavender discs.

    The three say different kinds of thing — where we are, how we work, how fast we
    answer — and giving them one colour each is what lets the strip be read at a glance
    instead of left to right. The classes are literals rather than an interpolated
    `text-${accent}` because Tailwind scans source text for class names and would find
    nothing to generate in a template string.
  */
  const highlights = [
    offices.length > 0
      ? {
          icon: MapPin,
          label: offices.join(' · '),
          accent: 'bg-accent-teal-soft text-accent-teal',
        }
      : null,
    // True of every search we run, and the reason senior candidates take the call.
    { icon: ShieldCheck, label: 'Confidential search', accent: 'bg-outline text-primary-strong' },
    settings.contact.responsePromise
      ? {
          icon: Clock,
          label: 'One business day reply',
          accent: 'bg-accent-amber-soft text-accent-amber',
        }
      : null,
  ].filter((item) => item !== null);

  if (highlights.length === 0) return null;

  return (
    // Kept attached to the content above rather than pinned to the hero's bottom edge.
    // Pinned, any spare viewport height opened up between the buttons and this strip and
    // read as a hole; trailing the content, the same space sits below everything and
    // reads as padding.
    <div className="mt-8 w-full">
      {/*
        Full container width, below both columns. Still a gradient rather than a flat
        border — solid at the ends, lighter through the middle — so it reads as the base
        of the hero without drawing a hard line under the illustration.
      */}
      <div
        aria-hidden="true"
        className="h-px w-full bg-gradient-to-r from-outline-strong/60 via-outline-strong/25 to-outline-strong/60"
      />
      <ul className="mt-5 flex flex-wrap items-center gap-x-7 gap-y-4">
        {highlights.map((item) => {
          const Icon = item.icon;
          return (
            <li key={item.label} className="flex items-center gap-2.5">
              <span
                aria-hidden="true"
                className={cn('grid size-8 shrink-0 place-items-center rounded-full', item.accent)}
              >
                <Icon className="size-4" />
              </span>
              <span className="text-[13px] font-medium text-body">{item.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Capability grid
// -----------------------------------------------------------------------------

/**
 * Six capability cards — the design's "Supercharge your hiring pipeline" grid.
 *
 * This is the block that needed the most rewriting. The original six were Automated
 * Sourcing, Rapid Screening, Verified Profiles, Predictive Analytics, Seamless
 * Onboarding and Cultural Alignment — a feature list for software. These six describe
 * the same six columns of work as things people do.
 *
 * The accent per card is positional, straight from the design: cards two and five are
 * the tinted ones, with an inset glow and a coloured border; the rest are white with a
 * hairline. Bodies are kept inside three lines so the `line-clamp` that keeps the row
 * even never actually hides a word.
 */
const CAPABILITIES = [
  {
    icon: Compass,
    title: 'Mapped, not scraped',
    body: 'We build a picture of who is actually doing the job elsewhere, including the people who are not looking.',
  },
  {
    icon: FileSearch,
    title: 'Structured screening',
    body: 'The same questions, in the same order, scored the same way — so a shortlist is comparable rather than a set of impressions.',
  },
  {
    icon: Users,
    title: 'References that mean something',
    body: 'We speak to people who managed the work, not to a list the candidate supplied, and we tell you what we heard.',
  },
  {
    icon: Layers,
    title: 'Market intelligence',
    body: 'What the role really pays, how long it takes to fill, and which requirement on your brief is costing you the pipeline.',
  },
  {
    icon: Clock,
    title: 'Offer to start date',
    body: 'Notice periods, counter-offers and relocation are where placements are lost. We stay involved until the person is at their desk.',
  },
  {
    icon: Building2,
    title: 'Fit for how you work',
    body: 'A judgement about whether someone will thrive in your organisation, from people who have taken the time to understand it.',
  },
] as const;

const CARD_BASE_SHADOW = 'shadow-[0_1px_3px_rgba(16,24,40,0.05)]';

const CAPABILITY_ACCENTS = [
  {
    card: `border-hairline ${CARD_BASE_SHADOW} hover:border-indigo-200 hover:shadow-[0_10px_25px_rgba(79,70,229,0.1)]`,
    tile: 'from-indigo-500 to-indigo-600',
  },
  {
    card: 'border-amber-500/40 shadow-[0_1px_3px_rgba(16,24,40,0.05),inset_0_0_20px_rgba(245,158,11,0.05)] hover:border-amber-400 hover:shadow-[0_10px_25px_rgba(245,158,11,0.15)]',
    tile: 'from-amber-400 to-amber-500',
  },
  {
    card: `border-hairline ${CARD_BASE_SHADOW} hover:border-emerald-200 hover:shadow-[0_10px_25px_rgba(16,185,129,0.1)]`,
    tile: 'from-emerald-400 to-emerald-500',
  },
  {
    card: `border-hairline ${CARD_BASE_SHADOW} hover:border-violet-200 hover:shadow-[0_10px_25px_rgba(139,92,246,0.1)]`,
    tile: 'from-violet-500 to-violet-600',
  },
  {
    card: 'border-green-500/40 shadow-[0_1px_3px_rgba(16,24,40,0.05),inset_0_0_20px_rgba(34,197,94,0.05)] hover:border-green-400 hover:shadow-[0_10px_25px_rgba(34,197,94,0.15)]',
    tile: 'from-green-500 to-green-600',
  },
  {
    card: `border-hairline ${CARD_BASE_SHADOW} hover:border-rose-200 hover:shadow-[0_10px_25px_rgba(244,63,94,0.1)]`,
    tile: 'from-rose-400 to-rose-500',
  },
] as const;

export function CapabilityGrid(): ReactNode {
  const { settings } = useSite();
  if (!settings.featureFlags.showCapabilityGrid) return null;

  return (
    <section
      className={cn('relative z-10 w-full border-t border-gray-100 bg-white', sectionRhythm)}
    >
      <Container pad="wide" className="flex flex-col items-center">
        <div className="mx-auto mb-12 max-w-[700px] text-center">
          <h2 className="mb-4 text-[38px] font-extrabold leading-tight tracking-tight text-heading">
            Recruitment done properly is{' '}
            <span className="bg-gradient-to-r from-violet-600 to-blue-500 bg-clip-text text-transparent">
              unglamorous
            </span>
          </h2>
          <p className="mx-auto max-w-[620px] text-[15px] leading-relaxed text-copy">
            There is no shortcut we are hiding. There is a process, run consistently, by people who
            have hired for the role before.
          </p>
        </div>

        <div className="grid w-full max-w-[1100px] grid-cols-1 gap-6 md:grid-cols-3">
          {CAPABILITIES.map((item, index) => {
            const accent = CAPABILITY_ACCENTS[index % CAPABILITY_ACCENTS.length]!;
            const Icon = item.icon;

            return (
              <div
                key={item.title}
                className={cn(
                  'rounded-[16px] border bg-white p-[28px] transition-all duration-300 hover:-translate-y-[2px]',
                  accent.card,
                )}
              >
                <div
                  className={cn(
                    'mb-[14px] flex size-[40px] items-center justify-center rounded-[12px] bg-gradient-to-br',
                    accent.tile,
                  )}
                >
                  <Icon className="size-5 text-white" aria-hidden="true" />
                </div>
                <h3 className="mb-2 text-[17px] font-semibold text-title">{item.title}</h3>
                <p className="mb-[28px] line-clamp-3 text-[13px] leading-[1.6] text-copy">
                  {item.body}
                </p>
                <div aria-hidden="true" className="h-px w-full bg-hairline-soft" />
              </div>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Statistics band
// -----------------------------------------------------------------------------

/**
 * The headline figures band.
 *
 * Hidden until the client supplies numbers it can stand behind. The design hard-coded
 * "500+ placements / 98% retention rate / 14 days to hire / 50+ cities covered" — for a
 * firm that described having four or five clients. Those are not placeholders; they are
 * claims, and the honest default is to show nothing. Turning `stats.enabled` on in the
 * admin renders this exactly as designed, dividers and all.
 */
/**
 * One figure in the band, counting up from zero the first time it is scrolled to.
 *
 * Two copies of the value, deliberately. The visible one is overwritten frame by frame
 * by `useCountUp`, so it is hidden from assistive technology — a screen reader reaching
 * the band mid-count would otherwise announce a figure that is not the real one. The
 * `sr-only` copy is the value as the client wrote it, and it never changes.
 */
function StatValue({ value }: { value: string }): ReactNode {
  const countUp = useCountUp(value);

  return (
    <>
      <span aria-hidden="true" ref={countUp}>
        {value}
      </span>
      <span className="sr-only">{value}</span>
    </>
  );
}

export function StatsBand(): ReactNode {
  const { settings } = useSite();
  const stats = settings.stats;

  if (!stats.enabled || stats.items.length === 0) return null;

  return (
    <section className={cn('bg-surface', sectionRhythm)}>
      <Container pad="gutter">
        <div className="flex flex-wrap items-center justify-around rounded-[24px] bg-gradient-to-br from-band-from via-band-via to-band-to px-8 py-12">
          {stats.items.map((stat, index) => (
            <div
              key={stat.label}
              className={cn(
                'relative w-full text-center sm:w-1/2 md:w-1/4',
                // The stacked columns are spaced apart, but the last one adds no gap
                // below itself — that would read as uneven padding inside the card.
                index < stats.items.length - 1 && 'mb-8 md:mb-0',
                // A hairline between columns, on the same "not after the last one"
                // rule, and only once the columns actually sit side by side.
                index < stats.items.length - 1 &&
                  "after:absolute after:right-0 after:top-1/2 after:hidden after:h-12 after:w-px after:-translate-y-1/2 after:bg-white/10 after:content-[''] md:after:block",
              )}
            >
              <div className="mb-2 text-[40px] font-bold leading-none tabular-nums text-white">
                <StatValue value={stat.value} />
              </div>
              <div className="text-[12px] font-bold uppercase tracking-widest text-white/70">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Split feature sections (the two "orbit" blocks)
// -----------------------------------------------------------------------------

/**
 * The orbit's authoring space.
 *
 * Chip centres and the SVG's viewBox are both expressed in it, and the rendered box keeps
 * this aspect ratio at every breakpoint — so one set of coordinates describes the layout
 * at any size and the whole visual scales as a unit.
 */
const ORBIT = { width: 380, height: 340 } as const;

interface OrbitChip {
  label: string;
  icon: ComponentType<{ className?: string }>;
  /**
   * The centre of the chip, in `ORBIT` coordinates.
   *
   * This replaced a pair of Tailwind edge offsets (`right-[40px] top-[50px]`) alongside a
   * *separate* `connectors` list of line endpoints. Nothing tied the two together, so the
   * dashed lines stopped short of the chips they were supposed to reach — visible in the
   * rendered section, and guaranteed to drift again the moment either list was edited.
   * One coordinate now positions the chip and terminates its line.
   */
  at: readonly [number, number];
  colour: string;
}

interface OrbitTheme {
  /** The section's top-to-bottom wash. */
  section: string;
  blob: string;
  node: string;
  badge: string;
  check: string;
  button: string;
}

interface SplitFeatureProps {
  theme: OrbitTheme;
  eyebrow: string;
  title: string;
  body: string;
  points: readonly string[];
  ctaLabel: string;
  ctaHref: string;
  nodeIcon: ComponentType<{ className?: string }>;
  chips: readonly OrbitChip[];
  /** Puts the visual on the left, as the design's mint section does. */
  visualFirst?: boolean;
  className?: string;
}

function SplitFeature({
  theme,
  eyebrow,
  title,
  body,
  points,
  ctaLabel,
  ctaHref,
  nodeIcon: NodeIcon,
  chips,
  visualFirst = false,
  className,
}: SplitFeatureProps): ReactNode {
  return (
    <section className={cn('relative overflow-hidden', sectionRhythm, theme.section, className)}>
      <Container pad="wide" className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
        <div
          className={cn(
            // The reference sets `justify-start`/`justify-end` here, but every child of
            // this box is absolutely positioned, so `justify-*` moves nothing. What
            // actually places the box in its 556px column is the margin: `mx-auto`
            // centres it while stacked, then one side is pinned at `lg` so the orbit sits
            // on the section's outer edge rather than against the text.
            //
            // Wider than the design's 380px, at the same 380:340 ratio. At the original
            // size the chips had to be set in 11px type to fit between the rings, which
            // was the smallest text on the site by some margin and sat in the emptiest
            // part of it. Holding the ratio is what lets the SVG's viewBox and the chips'
            // percentage offsets stay in one coordinate space across all three sizes.
            'relative mx-auto flex aspect-[38/34] w-full max-w-[400px] md:max-w-[460px] lg:max-w-[500px]',
            visualFirst ? 'order-2 lg:order-1 lg:ml-0' : 'order-2 lg:mr-0',
          )}
        >
          {/*
            The blob and the two rings, as fractions of the box's width rather than fixed
            pixels — `w-[…] aspect-square`, not `size-[…]`, because a percentage passed to
            `size` resolves height against the box's *height* and turns each circle into
            an ellipse. The fractions are the design's 300/280/180 over its 380 width.
          */}
          <div
            aria-hidden="true"
            className={cn(
              'absolute left-1/2 top-1/2 aspect-square w-[79%] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[60px]',
              theme.blob,
            )}
          />
          <div
            aria-hidden="true"
            className="absolute left-1/2 top-1/2 aspect-square w-[74%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-gray-300/50"
          />
          <div
            aria-hidden="true"
            className="absolute left-1/2 top-1/2 aspect-square w-[47%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-gray-300/60"
          />

          <svg
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 size-full"
            viewBox={`0 0 ${ORBIT.width} ${ORBIT.height}`}
          >
            {chips.map((chip) => (
              <line
                key={chip.label}
                x1={ORBIT.width / 2}
                y1={ORBIT.height / 2}
                x2={chip.at[0]}
                y2={chip.at[1]}
                strokeDasharray="4"
                strokeWidth="1.25"
                className="stroke-gray-300"
              />
            ))}
          </svg>

          <div
            className={cn(
              'absolute left-1/2 top-1/2 z-10 flex size-[72px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[20px] bg-gradient-to-br shadow-lg',
              theme.node,
            )}
          >
            <NodeIcon className="size-8 text-white" />
          </div>

          {/*
            Each chip is centred on its own coordinate, so it sits exactly where its dashed
            line ends. `whitespace-nowrap` because a chip that wraps to two lines stops
            being a chip, and the labels are short enough that it will not need to.
          */}
          {chips.map((chip) => {
            const ChipIcon = chip.icon;
            return (
              <div
                key={chip.label}
                style={{
                  left: `${(chip.at[0] / ORBIT.width) * 100}%`,
                  top: `${(chip.at[1] / ORBIT.height) * 100}%`,
                }}
                className="absolute z-10 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2.5 whitespace-nowrap rounded-full border border-hairline bg-white px-4 py-2.5 shadow-[0_4px_14px_rgba(16,24,40,0.1)]"
              >
                <ChipIcon className={cn('size-[18px] shrink-0', chip.colour)} />
                <span className="text-[13px] font-semibold text-gray-700">{chip.label}</span>
              </div>
            );
          })}
        </div>

        {/*
          `order-1` in both branches so the heading introduces the visual while stacked.
          At `lg` the text takes the column the orbit vacated and hugs the opposite edge,
          which is what makes the two sections read as a deliberate zig-zag instead of two
          near-identical rows whose text starts 76px apart.
        */}
        <div
          className={cn('max-w-[520px]', visualFirst ? 'order-1 lg:order-2 lg:ml-auto' : 'order-1')}
        >
          <span
            className={cn(
              'mb-4 inline-block rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest',
              theme.badge,
            )}
          >
            {eyebrow}
          </span>

          {/*
            No `line-clamp` on either of these any more. The design's clamps were sized for
            the design's shorter copy: at two lines the heading cut "What a role pays, and
            what it really takes" in half, and at three lines the body lost the end of the
            sentence that carries the point. A heading that truncates is a bug, not a
            layout, and the grid stretches to whichever column is taller regardless.
          */}
          <h2 className="mb-6 text-[36px] font-extrabold leading-[1.15] text-heading">{title}</h2>

          <p className="mb-8 text-[16px] leading-relaxed text-copy">{body}</p>

          <ul className="mb-8 space-y-[12px]">
            {points.map((point) => (
              <li key={point} className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex size-[22px] shrink-0 items-center justify-center rounded-[6px]',
                    theme.check,
                  )}
                >
                  <Check className="size-4 text-white" strokeWidth={3} />
                </span>
                <span className="text-[15px] font-medium text-copy-strong">{point}</span>
              </li>
            ))}
          </ul>

          <ButtonLink
            to={ctaHref}
            className={cn('h-auto gap-3 px-6 py-3 text-[15px]', theme.button)}
          >
            {ctaLabel}
            <span
              aria-hidden="true"
              className="flex size-6 items-center justify-center rounded-full bg-white/20"
            >
              <ArrowRight className="size-3.5" />
            </span>
          </ButtonLink>
        </div>
      </Container>
    </section>
  );
}

/**
 * The design's "Evaluate with absolute certainty / Precision Screening" section.
 *
 * Retitled: "absolute certainty" is not something a recruiter can offer, and claiming
 * it is exactly the register the client asked to move away from. Everything else — the
 * mint wash, the orbit, the chip positions, the emerald checklist — is the design's.
 */
export function ProcessSection(): ReactNode {
  const { settings } = useSite();
  if (!settings.featureFlags.showProcessSection) return null;

  return (
    <SplitFeature
      visualFirst
      theme={{
        section: 'bg-gradient-to-b from-violet-50 to-white',
        blob: 'bg-violet-200/50',
        node: 'from-violet-500 to-indigo-600',
        badge: 'bg-violet-100 text-violet-700',
        check: 'bg-violet-500',
        button:
          'from-violet-500 to-indigo-500 shadow-[0_8px_16px_rgba(124,58,237,0.25)] hover:shadow-[0_12px_20px_rgba(124,58,237,0.35)]',
      }}
      eyebrow="Our process"
      title="Fewer candidates, better reasons"
      body="A shortlist of four people we can each justify is more useful than twenty CVs matching on keywords. We present less, explain more, and say when we are not confident."
      points={[
        'A written brief we both agree describes the same role',
        'Structured interviews, scored against that brief',
        'References from people who managed the work',
      ]}
      ctaLabel="Talk to us about a role"
      ctaHref="/contact"
      nodeIcon={ClipboardCheck}
      chips={[
        { label: 'Brief agreed', icon: FileSearch, at: [268, 74], colour: 'text-violet-600' },
        { label: 'Market mapped', icon: Compass, at: [280, 258], colour: 'text-indigo-600' },
        {
          label: 'Interviews scored',
          icon: MessagesSquare,
          at: [104, 268],
          colour: 'text-violet-500',
        },
        { label: 'References taken', icon: Users, at: [96, 80], colour: 'text-indigo-400' },
      ]}
    />
  );
}

/**
 * The design's "Data-driven talent strategy" section, which promised live pipeline
 * dashboards and an analytics view.
 *
 * Those are product features. What the firm genuinely offers is the knowledge that
 * comes from running searches in these markets, so the section offers that instead — no
 * dashboard, and no "View Analytics" link to a screen that does not exist.
 */
export function InsightsSection(): ReactNode {
  const { settings } = useSite();
  if (!settings.featureFlags.showInsightsSection) return null;

  return (
    <SplitFeature
      className="border-b border-gray-100"
      theme={{
        section: 'bg-gradient-to-b from-purple-50 to-white',
        blob: 'bg-purple-300/40',
        node: 'from-purple-500 to-fuchsia-600',
        badge: 'bg-purple-100 text-purple-700',
        check: 'bg-purple-500',
        button:
          'from-purple-600 to-fuchsia-600 shadow-[0_8px_16px_rgba(168,85,247,0.25)] hover:shadow-[0_12px_20px_rgba(168,85,247,0.35)]',
      }}
      eyebrow="What we know"
      title="What a role pays, and what it really takes"
      body="Most stalled searches are not a sourcing problem. They are a brief asking for an intersection of requirements that only a dozen people satisfy, half of whom are not moving this year."
      points={[
        'Realistic compensation ranges for the market, not the aspiration',
        'Which requirement is load-bearing and which is a preference',
        'Notice periods and start dates that hold across the UAE and India',
      ]}
      ctaLabel="Read our notes"
      ctaHref="/blog"
      nodeIcon={Activity}
      chips={[
        { label: 'Salary bands', icon: Banknote, at: [268, 74], colour: 'text-purple-600' },
        { label: 'Notice periods', icon: Hourglass, at: [280, 258], colour: 'text-fuchsia-600' },
        { label: 'Counter-offers', icon: TrendingUp, at: [104, 268], colour: 'text-pink-500' },
        { label: 'Time to fill', icon: PieChart, at: [96, 80], colour: 'text-indigo-500' },
      ]}
    />
  );
}

// -----------------------------------------------------------------------------
// Services
// -----------------------------------------------------------------------------

/**
 * The four services, exactly as the client named them on the call.
 *
 * Note the fourth card. The design said "Contract Staffing"; the brief said on-demand
 * talent sourcing — "rent a recruiter". Contract staffing was mentioned in passing, so
 * it appears as a capability of that service rather than as a peer to it.
 *
 * One record per service, feeding three surfaces: the four-card grid on the homepage and
 * the services index (`title`, `summary`, `icon`), and the whole of that service's own
 * page (everything else). Keeping it in one list rather than four page components is what
 * makes the grid and the page it links to incapable of describing the service differently
 * — and `as const` means adding a field to one service is a type error until the other
 * three have it too.
 *
 * The page-only fields:
 *
 * - `alias` — the client's alternative name for the service, where they have one. Shown
 *   next to the title, never instead of it; see the note on the field below.
 * - `intro` — the opening paragraphs, in the client's own words.
 * - `offersHeading` / `offers` — the "what you get" cards. The heading varies per service
 *   because the client wrote it differently each time and the difference is meaningful:
 *   what we *offer* is not what we *do*.
 * - `howItWorks` — the closing paragraph.
 */
export const SERVICES = [
  {
    slug: ServiceSlug.EXECUTIVE_SEARCH,
    headline: 'Senior hires you can justify',
    headlineHighlight: 'you can justify',
    idealFor: 'Ideal for: CXO, board and functional-head appointments.',
    icon: UserSearch,
    title: 'Executive search',
    /** No second name for this one — see the note on `alias` below. */
    alias: null,
    summary:
      'Confidential search for senior and board-level appointments, including the people who are not looking.',
    detail:
      'Senior hires fail on fit far more often than on capability. We spend the time up front to understand what the role has to change in your organisation, then map the market properly rather than working a network.',
    intro: [
      'Hiring senior leaders does not mean just filling a role. It is about finding people who understand your mission and share your values, and who know how to lead with purpose.',
      'We help you hire executives who fit your company, not just the job description.',
    ],
    offersHeading: 'What we offer',
    offers: [
      {
        icon: Globe,
        title: 'Global AI talent sourcing',
        body: 'We search across markets to find experienced leaders who match your goals.',
      },
      {
        icon: ClipboardCheck,
        title: 'Role-based evaluation',
        body: 'Every candidate is assessed against what the role actually needs, not against generic criteria.',
      },
    ],
    howItWorks:
      'We keep hiring clear and quick, so you get the right people without the hassle and the business keeps moving. From first outreach to final placement, the search stays aligned with your strategy.',
    points: [
      'Confidential approach to passive candidates',
      'Written brief agreed before the search opens',
      'Structured assessment against that brief',
      'Involvement through offer, notice and start',
    ],
  },
  {
    slug: ServiceSlug.PERMANENT_HIRING,
    headline: 'The same process, every time',
    headlineHighlight: 'every time',
    idealFor: 'Ideal for: teams hiring several comparable roles at once.',
    icon: Gem,
    title: 'Permanent hiring',
    /*
      The client's own name for this service. Shown alongside the title rather than
      replacing it: the navigation label and the page row's title live in the database
      (see `apps/api/src/database/seed.ts`), so renaming here would rename the card and
      leave every menu pointing at it under the old name.
    */
    alias: 'FTE placement',
    summary: 'Building your core team with people committed to where the business is going.',
    detail:
      'Volume permanent hiring where consistency matters more than any individual search. The same process every time, so your managers compare candidates rather than impressions.',
    intro: [
      'Hiring full-time employees should not be a hassle. We keep it steady and smart.',
      'We help you find the right people for long-term roles, without delays and without shortcuts. The process works across industries and adjusts to what you need.',
    ],
    offersHeading: 'How we do it',
    offers: [
      {
        icon: ScanSearch,
        title: 'Targeted AI sourcing and human screening',
        body: 'We look for candidates who match your role, not just your job title — with the actual screening transcript if you ask for it.',
      },
      {
        icon: HeartHandshake,
        title: 'Cultural fit hiring',
        body: 'We make sure they will work well with your team, not only on paper.',
      },
    ],
    howItWorks:
      'We stay with you at every step from first brief to signed offer: simple stages, honest feedback, and results that last. Local or global, we help you build strong teams through full-time placement.',
    points: [
      'Consistent, scorecard-based screening',
      'Shortlists you can actually compare',
      'Feedback on where your process loses people',
      'Replacement cover within an agreed period',
    ],
  },
  {
    slug: ServiceSlug.RPO,
    headline: 'Your hiring function, run properly',
    headlineHighlight: 'run properly',
    idealFor: 'Ideal for: organisations hiring continuously rather than in bursts.',
    icon: Timer,
    title: 'Recruitment process outsourcing',
    /** See the note on `alias` above — the database owns the navigation label. */
    alias: 'Rent a recruiter',
    summary: 'We run all or part of your hiring function, under your brand and your standards.',
    detail:
      'For teams hiring continuously enough that recruitment is a function rather than a project. We take on sourcing and screening; your managers keep the decision, because that is where the context lives.',
    intro: [
      'Need extra help with hiring? Rent an embedded recruiter: flexible access to hiring expertise — when you need them, for as long as you need them. No long contracts, no overhead, just support that fits your workflow.',
      'Whether you are scaling fast or filling a few key roles, our recruiters plug into your team and get to work.',
    ],
    offersHeading: 'What you get',
    offers: [
      {
        icon: Workflow,
        title: 'Process management',
        body: 'We handle the day-to-day hiring tasks so your team can stay focused on their own work.',
      },
      {
        icon: FileSearch,
        title: 'Candidate sourcing and screening',
        body: 'We find qualified candidates and screen them against your criteria, not ours.',
      },
      {
        icon: Zap,
        title: 'Fast-track hiring',
        body: 'We speed the process up without cutting corners, so you can hire quickly and still hire confidently.',
      },
    ],
    howItWorks:
      'You get recruitment support built around your goals, adapted to your timeline, your tools and your team structure. It is simple: you rent the expertise, we deliver the results.',
    points: [
      'Embedded team working as part of yours',
      'Your employer brand, not ours',
      'Reporting on pipeline health and time to hire',
      'Scales down as easily as up',
    ],
  },
  {
    slug: ServiceSlug.ON_DEMAND_SOURCING,
    headline: 'Sourcing capacity, when you need it',
    headlineHighlight: 'when you need it',
    idealFor: 'Ideal for: a hiring spike, a launch, or covering a vacant recruiter seat.',
    icon: BriefcaseBusiness,
    title: 'On-demand talent sourcing',
    /** No second name for this one — see the note on `alias` above. */
    alias: null,
    /*
      No "rent a recruiter" in this summary any more. That is the client's name for the
      embedded-recruiter service above, and with both spelled out on their own pages, two
      cards in the same four-card grid claiming the phrase read as one service listed
      twice. The distinction that survives: that service lends you a recruiter, this one
      delivers a pipeline.
    */
    summary:
      'Pipeline on tap: sourcing capacity for a defined period, including contract staffing.',
    detail:
      'When you need sourcing capacity for a quarter rather than a permanent hire in your talent team. A steady flow of screened candidates against your priorities, for an agreed period, with no long-term commitment.',
    intro: [
      'Finding the right candidates is a tough nut to crack, especially when you are hiring across markets or scaling fast. We make it easier.',
      'Our sourcing support helps you build a steady pipeline of qualified talent. We focus on what matters: speed, clarity and fit.',
    ],
    offersHeading: 'What we do',
    offers: [
      {
        icon: Globe,
        title: 'Global talent sourcing',
        body: 'We source talent globally, with local insight and market-specific strategies.',
      },
      {
        icon: ClipboardCheck,
        title: 'Clear evaluation steps',
        body: 'Every candidate goes through a structured process, so you know exactly who you are getting and why they are a match.',
      },
      {
        icon: Users,
        title: 'Cultural fit techniques',
        body: "We look beyond skills. Our screening checks for alignment with your team's values and way of working.",
      },
    ],
    howItWorks:
      'You get sourcing support that is practical, flexible and built for the long term, so your team stays focused instead of guessing. You tell us what you need; we find the right people.',
    points: [
      'Dedicated sourcing capacity for an agreed period',
      'Works inside your systems and process',
      'Contract and interim staffing covered',
      'Fixed monthly cost, not per placement',
    ],
  },
] as const;

/** Card icon and link colour per service, positional, from the design's four cards. */
const SERVICE_ACCENTS = [
  { tile: 'bg-blue-50', icon: 'text-blue-600', link: 'text-blue-600' },
  { tile: 'bg-purple-50', icon: 'text-primary', link: 'text-primary' },
  { tile: 'bg-green-50', icon: 'text-green-600', link: 'text-green-600' },
  { tile: 'bg-orange-50', icon: 'text-orange-500', link: 'text-orange-500' },
] as const;

export function ServiceCards({
  heading = true,
  className,
}: {
  heading?: boolean;
  /** Lets a page that puts this straight under its header band trim the top padding. */
  className?: string;
}): ReactNode {
  return (
    <section className={cn('bg-surface-wash', sectionRhythm, className)}>
      <Container pad="gutter">
        {heading ? (
          <div className="mb-12 text-center">
            <span className="mb-4 block text-[11px] font-bold uppercase tracking-widest text-primary">
              What we do
            </span>
            <h2 className="text-[40px] font-bold text-heading">Four ways we help you hire</h2>
            <p className="mx-auto mt-4 max-w-[620px] text-[15px] leading-relaxed text-copy">
              Pick the one that matches the problem. We will tell you if it is the wrong one.
            </p>
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((service, index) => {
            const accent = SERVICE_ACCENTS[index % SERVICE_ACCENTS.length]!;
            const Icon = service.icon;

            return (
              <div
                key={service.slug}
                className="flex h-full flex-col rounded-[24px] bg-surface-raised p-8 shadow-soft transition-transform duration-300 hover:-translate-y-1"
              >
                <div
                  className={cn(
                    'mb-6 flex size-12 items-center justify-center rounded-xl',
                    accent.tile,
                  )}
                >
                  <Icon className={cn('size-5', accent.icon)} aria-hidden="true" />
                </div>
                <h3 className="mb-4 text-[20px] font-bold text-heading">{service.title}</h3>
                <p className="mb-8 text-[14px] leading-relaxed text-body">{service.summary}</p>
                <a
                  href={`/services/${service.slug}`}
                  className={cn(
                    'mt-auto flex items-center gap-1 text-[14px] font-semibold transition-all hover:gap-2',
                    accent.link,
                  )}
                >
                  Learn more
                  <ArrowRight className="size-4" />
                </a>
              </div>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Recruitment process — the services index
// -----------------------------------------------------------------------------

/**
 * The three stages of an engagement, as three numbered cards.
 *
 * Not behind a feature flag, unlike almost every band around it. The flagged sections are
 * *marketing* bands — a statistics wall, a logo wall, a process teaser — that the client
 * may want off. This is the services page explaining what buying a service involves, so a
 * toggle that could leave the page without it is a toggle that breaks the page. Same
 * reasoning for `StrategicFocus`, `OurStory`, `MissionVision` and `FocusPillars` below.
 */
const PROCESS_STAGES = [
  {
    icon: Target,
    title: 'Evaluate',
    body: 'We start by understanding your goals, your team setup and the kind of people you need. That is what lets us find candidates who actually fit, rather than candidates who merely match.',
  },
  {
    icon: Sparkles,
    title: 'Match',
    body: 'We use AI-powered recruitment tools to search a global pool of 50,000+ pre-vetted professionals, then read past the skills — mindset and values decide whether a good hire stays a good hire.',
  },
  {
    icon: Rocket,
    title: 'Results',
    body: 'You get executive-level talent ready for interviews in 48 hours. Focused, and built to hold up over the long term in the GCC and beyond.',
  },
] as const;

/**
 * Per-stage accent, positional, in the family the rest of the site uses.
 *
 * A gradient tile and a matching numeral rather than the flat tiles `SERVICE_ACCENTS`
 * uses: these three are a sequence, and the numeral is what says so.
 */
const PROCESS_ACCENTS = [
  { tile: 'from-blue-500 to-indigo-600', numeral: 'text-blue-100', ring: 'hover:border-blue-200' },
  {
    tile: 'from-violet-500 to-purple-600',
    numeral: 'text-violet-100',
    ring: 'hover:border-violet-200',
  },
  {
    tile: 'from-emerald-500 to-teal-600',
    numeral: 'text-emerald-100',
    ring: 'hover:border-emerald-200',
  },
] as const;

export function RecruitmentProcess(): ReactNode {
  return (
    <section className={cn('border-t border-gray-100 bg-surface', sectionRhythm)}>
      <Container pad="gutter">
        <div className="mx-auto mb-12 max-w-[700px] text-center">
          <span className="mb-4 block text-[11px] font-bold uppercase tracking-widest text-primary">
            How an engagement runs
          </span>
          <h2 className="mb-4 text-[38px] font-extrabold leading-tight tracking-tight text-heading">
            Our optimised{' '}
            <span className="bg-gradient-to-r from-violet-600 to-blue-500 bg-clip-text text-transparent">
              recruitment process
            </span>
          </h2>
          <p className="mx-auto max-w-[620px] text-[15px] leading-relaxed text-copy">
            Three stages, the same three every time — so you always know which one your search is in
            and what has to happen for it to reach the next.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {PROCESS_STAGES.map((stage, index) => {
            const accent = PROCESS_ACCENTS[index % PROCESS_ACCENTS.length]!;
            const Icon = stage.icon;

            return (
              <div
                key={stage.title}
                className={cn(
                  'group relative flex h-full flex-col overflow-hidden rounded-[24px] border border-hairline bg-surface-raised p-8',
                  CARD_BASE_SHADOW,
                  'transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_25px_rgba(16,24,40,0.1)]',
                  accent.ring,
                )}
              >
                {/*
                  The stage number, set large and pale behind the content rather than in a
                  badge beside it. It is an ordinal, not a label — it needs to be legible
                  at a glance and to disappear when you are reading the words.
                */}
                <span
                  aria-hidden="true"
                  className={cn(
                    'pointer-events-none absolute -right-2 -top-6 select-none text-[110px] font-black leading-none transition-transform duration-300 group-hover:scale-105',
                    accent.numeral,
                  )}
                >
                  {index + 1}
                </span>

                <div
                  className={cn(
                    'relative mb-6 flex size-12 items-center justify-center rounded-[14px] bg-gradient-to-br',
                    accent.tile,
                  )}
                >
                  <Icon className="size-5 text-white" aria-hidden="true" />
                </div>

                <h3 className="relative mb-3 text-[20px] font-bold text-heading">{stage.title}</h3>
                <p className="relative text-[14px] leading-relaxed text-copy">{stage.body}</p>
              </div>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Strategic focus — the services index
// -----------------------------------------------------------------------------

/**
 * The closing statement on the services page: what the four services have in common.
 *
 * A single centred band rather than cards. It is one thought, and breaking one thought
 * into three tiles is the habit that turns a page into a wall of boxes.
 */
export function StrategicFocus(): ReactNode {
  const reveal = useReveal<HTMLDivElement>();

  return (
    <section className={cn('relative overflow-hidden bg-surface-wash', sectionRhythm)}>
      <Glow className="left-1/2 top-0 size-[520px] -translate-x-1/2 -translate-y-1/3" />

      <Container pad="gutter" className="relative">
        <div ref={reveal} className="mx-auto max-w-[760px] text-center">
          <span className="mb-4 block text-[11px] font-bold uppercase tracking-widest text-primary">
            Why it works
          </span>
          <h2 className="mb-6 text-[38px] font-extrabold leading-[1.15] tracking-tight text-heading">
            Your hiring goals.{' '}
            <span className="bg-gradient-to-r from-violet-600 to-blue-500 bg-clip-text text-transparent">
              Our strategic focus.
            </span>
          </h2>
          <p className="mb-10 text-[16px] leading-relaxed text-body">
            We combine smart recruitment technology with clear processes to help enterprises hire
            the right people, with purpose. Every role we fill has to support the culture you have
            and the strategy you are working towards. That is what makes us a trusted AI-powered
            recruitment agency rather than another supplier of CVs.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <ButtonLink to="/contact" size="lg">
              Talk to us about a role
            </ButtonLink>
            <ButtonLink to="/about" variant="secondary" size="lg">
              How we work
            </ButtonLink>
          </div>
        </div>
      </Container>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Our story — the About page
// -----------------------------------------------------------------------------

/**
 * The founding story, as a single column of prose.
 *
 * Deliberately not a card grid. It is a narrative in the client's own voice, and chopping
 * a narrative into tiles removes the one thing it has that a tile cannot carry, which is
 * the order the sentences come in. The pull-quote at the end is the sentence the client
 * ended on, given the weight they gave it out loud.
 */
export function OurStory(): ReactNode {
  const reveal = useReveal<HTMLDivElement>();

  return (
    // The lavender wash rather than white, and so no top hairline: it now sits between
    // `FocusPillars` and the team, both of which are white, and three white bands in a row
    // separated only by hairlines read as one very long section.
    <section className={cn('bg-surface-wash', sectionRhythm)}>
      <Container pad="gutter">
        <div ref={reveal} className="mx-auto max-w-[760px]">
          <div className="mb-10 text-center">
            <span className="mb-4 block text-[11px] font-bold uppercase tracking-widest text-primary">
              Our story
            </span>
            <h2 className="text-[38px] font-extrabold leading-tight tracking-tight text-heading">
              It started with one idea
            </h2>
          </div>

          <div className="flex flex-col gap-5 text-[16px] leading-relaxed text-body">
            <p>
              At The BlackBox Talent we started with a simple idea: hiring should be honest, smart,
              and tied to real business goals.
            </p>
            <p>
              In a world flooded with resumes and recruitment noise, we saw a gap between what
              companies needed and what traditional agencies delivered. Most agencies focus on
              filling roles fast. What we found missing was the harder part — real understanding.
            </p>
            <p>
              So we built a platform that combines trusted values with AI talent sourcing. It does
              not just match skills to roles. It looks at context, culture, and whether the fit will
              still be a fit in two years.
            </p>
            <p>
              We have helped companies across the GCC, Africa, the UK, Europe, India and other
              global markets build strong teams — from executive recruitment solutions to full-time
              employee placement. Every process is clear. Every candidate is pre-vetted. Every hire
              is backed by strategy.
            </p>
          </div>

          {/*
            The client's closing line, set as a quote rather than as a fifth paragraph. It
            is the thesis of everything above it, and a paragraph break alone was not
            enough to stop it reading as one more item in a list.
          */}
          <blockquote className="mt-10 border-l-[3px] border-primary/40 pl-6 text-[19px] font-semibold leading-[1.5] text-heading">
            We are not here to fill seats. We are here to build teams that lead, grow, and stay.
          </blockquote>
        </div>
      </Container>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Mission and vision — the About page
// -----------------------------------------------------------------------------

const MISSION_VISION = [
  {
    icon: Target,
    eyebrow: 'Our mission',
    title: 'Hire for fit, not just for skills',
    body: 'We help companies hire people who are a good fit — not just by skills, but by mindset and working style. Using AI-powered assessments and smart sourcing strategies, we make hiring faster and more focused, and every search is run with your business need in front of it. The mission is simple: bring high-performing teams and pre-vetted professionals together, the smart way.',
    accent: 'from-violet-500 to-purple-600',
    wash: 'from-violet-50 to-white',
  },
  {
    icon: Telescope,
    eyebrow: 'Our vision',
    title: 'Recruitment worth trusting',
    body: 'We want to make recruitment better with the help of advanced behavioural and psychometric assessment tools. That means building real relationships, understanding what each role actually needs, and placing people where they can grow. Honest hiring, clear assessment, long-term impact — so every placement still feels right a year later.',
    accent: 'from-blue-500 to-indigo-600',
    wash: 'from-blue-50 to-white',
  },
] as const;

export function MissionVision(): ReactNode {
  return (
    <section className={cn('bg-surface-wash', sectionRhythm)}>
      <Container pad="gutter">
        {/*
          Two columns, not three. These are a pair — where we are going and how we intend
          to get there — and a two-up grid is what says they are meant to be read against
          each other.
        */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {MISSION_VISION.map((item) => {
            const Icon = item.icon;

            return (
              <div
                key={item.eyebrow}
                className={cn(
                  'flex h-full flex-col rounded-[24px] border border-hairline bg-gradient-to-b p-8 md:p-10',
                  item.wash,
                  CARD_BASE_SHADOW,
                  'transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_25px_rgba(16,24,40,0.1)]',
                )}
              >
                <div
                  className={cn(
                    'mb-6 flex size-12 items-center justify-center rounded-[14px] bg-gradient-to-br',
                    item.accent,
                  )}
                >
                  <Icon className="size-5 text-white" aria-hidden="true" />
                </div>

                <span className="mb-3 block text-[11px] font-bold uppercase tracking-widest text-primary">
                  {item.eyebrow}
                </span>
                {/*
                  `h2`, not `h3`. This band has no heading of its own — the two eyebrows
                  say "our mission" and "our vision" and a heading above them would only
                  repeat that — so an `h3` here would sit under no `h2` at all and put a
                  hole in the page outline a screen reader navigates by.
                */}
                <h2 className="mb-4 text-[24px] font-bold leading-tight text-heading">
                  {item.title}
                </h2>
                <p className="text-[15px] leading-relaxed text-body">{item.body}</p>
              </div>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

// -----------------------------------------------------------------------------
// The three focuses — the About page
// -----------------------------------------------------------------------------

const FOCUS_PILLARS = [
  {
    icon: Cpu,
    title: 'Tech-forward',
    body: 'We fix what is broken in hiring. Most systems are slow and out of date. We use AI-powered sourcing tools to evaluate candidates quickly and accurately, then add the human insight that decides whether the match actually makes sense. Better decisions, made faster.',
  },
  {
    icon: HeartHandshake,
    title: 'Human-centred',
    body: 'Hiring is not about ticking boxes. It is about finding people who fit — how they think, how they work, how they get on with the people already there. We care about trust, which means being honest about what is working and what is not. That is why our process pairs AI talent sourcing with a human read, and why clients get candidates quickly.',
  },
  {
    icon: Globe,
    title: 'Global',
    body: 'We started in the GCC, working with companies that needed smart, scalable hiring. Our approach carries local values and global standards: ethical, efficient, and built for growth. We are proud to be recognised as one of the best recruitment agencies in the Middle East.',
  },
] as const;

/**
 * The palette for a row of two or three cards, positional.
 *
 * Shared by the About page's focus pillars and every service page's "what you get" grid,
 * and deliberately the same three hues as `PROCESS_ACCENTS` above — a visitor moving from
 * the services index to a service page to About should be looking at one site, and three
 * card rows that each invented their own colours is how that stops being true.
 */
const CARD_TRIO_ACCENTS = [
  { tile: 'from-blue-500 to-indigo-600', rule: 'bg-blue-500', ring: 'hover:border-blue-200' },
  { tile: 'from-violet-500 to-purple-600', rule: 'bg-violet-500', ring: 'hover:border-violet-200' },
  {
    tile: 'from-emerald-500 to-teal-600',
    rule: 'bg-emerald-500',
    ring: 'hover:border-emerald-200',
  },
] as const;

export function FocusPillars(): ReactNode {
  return (
    <section className={cn('border-t border-gray-100 bg-surface', sectionRhythm)}>
      <Container pad="gutter">
        <div className="mx-auto mb-12 max-w-[700px] text-center">
          <span className="mb-4 block text-[11px] font-bold uppercase tracking-widest text-primary">
            What we focus on
          </span>
          <h2 className="mb-4 text-[38px] font-extrabold leading-tight tracking-tight text-heading">
            Three things we will not compromise
          </h2>
          <p className="mx-auto max-w-[620px] text-[15px] leading-relaxed text-copy">
            The tools, the people, and the reach. Drop any one of the three and the other two stop
            being worth much.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {FOCUS_PILLARS.map((pillar, index) => {
            const accent = CARD_TRIO_ACCENTS[index % CARD_TRIO_ACCENTS.length]!;
            const Icon = pillar.icon;

            return (
              <div
                key={pillar.title}
                className={cn(
                  'flex h-full flex-col rounded-[24px] border border-hairline bg-surface-raised p-8',
                  CARD_BASE_SHADOW,
                  'transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_25px_rgba(16,24,40,0.1)]',
                  accent.ring,
                )}
              >
                <div className="mb-6 flex items-center gap-4">
                  <div
                    className={cn(
                      'flex size-12 shrink-0 items-center justify-center rounded-[14px] bg-gradient-to-br',
                      accent.tile,
                    )}
                  >
                    <Icon className="size-5 text-white" aria-hidden="true" />
                  </div>
                  {/*
                    A short rule instead of a number. These three are peers rather than a
                    sequence — unlike the process stages, which are numbered because the
                    order is the point.
                  */}
                  <span
                    aria-hidden="true"
                    className={cn('h-[3px] w-8 rounded-full', accent.rule)}
                  />
                </div>

                <h3 className="mb-3 text-[20px] font-bold text-heading">{pillar.title}</h3>
                <p className="text-[14px] leading-relaxed text-copy">{pillar.body}</p>
              </div>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Service detail body
// -----------------------------------------------------------------------------

/**
 * Everything on a service page between the header band and the closing cards.
 *
 * It lives here rather than in `StaticPages.tsx` because it is marketing layout, and the
 * first version of it was not: it was assembled from the generic `Section`/`Card`/
 * `SectionHeading` primitives, so the four service pages ended up with `text-h3`
 * headings and uniform lavender icon bubbles while every other page on the site uses a
 * pill eyebrow, a 38px extrabold heading and the accent palette above. The pages were
 * correct and read as a different site. Same three beats as before — the argument, what
 * you get, how it runs — in the house style, and sharing `CARD_TRIO_ACCENTS` with the
 * sections a visitor sees on either side of them.
 */
export function ServiceDetailBody({ service }: { service: (typeof SERVICES)[number] }): ReactNode {
  /*
    No `useReveal` anywhere in here, deliberately. An earlier version faded the intro in
    and left the two sections under it to appear instantly, which is worse than either
    choice made consistently — `SplitFeature` reveals nothing either, so a service page
    that animates only its first paragraph is the odd one out.
  */
  return (
    <>
      {/*
        The opening argument, set as a standfirst: centred, larger than body copy, and
        with the first paragraph carrying the most weight. No heading of its own — it is
        the continuation of the h1 directly above it, and a heading here would put a
        label between the page title and its own first sentence.
      */}
      <section className={cn('bg-surface', sectionRhythm)}>
        <Container pad="gutter">
          <div className="mx-auto max-w-[760px] text-center">
            {service.intro.map((paragraph, index) => (
              <p
                key={paragraph}
                className={cn(
                  'leading-relaxed',
                  index === 0
                    ? 'text-[20px] font-medium text-heading md:text-[22px]'
                    : 'mt-5 text-[16px] text-body',
                )}
              >
                {paragraph}
              </p>
            ))}
          </div>
        </Container>
      </section>

      {/* What you get. Two services list two of these and two list three, so the third
          column only appears at `lg` — a hard three-up left an empty cell on the pages
          with two, which reads as content that failed to load. */}
      <section className={cn('bg-surface-wash', sectionRhythm)}>
        <Container pad="gutter">
          <div className="mx-auto mb-12 max-w-[700px] text-center">
            {/*
              A fixed eyebrow over the service's own wording. "What you get" was the
              eyebrow here first, which put it directly above headings reading "What we
              offer" and "What you get" — the same phrase twice, once in each size.
            */}
            <span className="mb-4 block text-[11px] font-bold uppercase tracking-widest text-primary">
              Our approach
            </span>
            <h2 className="text-[38px] font-extrabold leading-tight tracking-tight text-heading">
              {service.offersHeading}
            </h2>
          </div>

          <div
            /*
              The row is narrowed to suit its own length rather than always filling the
              container. Two cards stretched across 1100px gave each one a 530px measure
              for two lines of 14px copy, which read as a mostly-empty box; 780px puts
              them at about the same width as one of the three-card pages.
            */
            className={cn(
              'mx-auto grid grid-cols-1 gap-6 sm:grid-cols-2',
              service.offers.length > 2 ? 'max-w-[1100px] lg:grid-cols-3' : 'max-w-[780px]',
            )}
          >
            {service.offers.map((offer, index) => {
              const accent = CARD_TRIO_ACCENTS[index % CARD_TRIO_ACCENTS.length]!;
              const Icon = offer.icon;

              return (
                <div
                  key={offer.title}
                  className={cn(
                    'flex h-full flex-col rounded-[24px] border border-hairline bg-surface-raised p-8',
                    CARD_BASE_SHADOW,
                    'transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_25px_rgba(16,24,40,0.1)]',
                    accent.ring,
                  )}
                >
                  <div className="mb-6 flex items-center gap-4">
                    <div
                      className={cn(
                        'flex size-12 shrink-0 items-center justify-center rounded-[14px] bg-gradient-to-br',
                        accent.tile,
                      )}
                    >
                      <Icon className="size-5 text-white" aria-hidden="true" />
                    </div>
                    <span
                      aria-hidden="true"
                      className={cn('h-[3px] w-8 rounded-full', accent.rule)}
                    />
                  </div>

                  <h3 className="mb-3 text-[20px] font-bold text-heading">{offer.title}</h3>
                  <p className="text-[14px] leading-relaxed text-copy">{offer.body}</p>
                </div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* How it runs, against what is in it. */}
      <section className={cn('border-t border-gray-100 bg-surface', sectionRhythm)}>
        <Container
          pad="gutter"
          className="mx-auto grid max-w-[1100px] grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:gap-14"
        >
          <div>
            <span className="mb-4 block text-[11px] font-bold uppercase tracking-widest text-primary">
              How it works
            </span>
            {/*
              One heading for all four rather than `{service.title}, end to end`, which
              restated the eyebrow directly above it and gave the longest service
              "Recruitment process outsourcing, end to end" — a mouthful, and the only one
              of the four that wrapped.
            */}
            <h2 className="mb-6 text-[38px] font-extrabold leading-[1.15] tracking-tight text-heading">
              What working with us looks like
            </h2>
            <p className="mb-5 text-[16px] leading-relaxed text-body">{service.detail}</p>
            {/*
              The client's account of the engagement, after ours. Two paragraphs rather
              than one merged one: the first is what we do, the second is what it is like
              to work with us, and they answer different questions.
            */}
            <p className="text-[16px] leading-relaxed text-body">{service.howItWorks}</p>
          </div>

          {/*
            The checklist as a raised panel rather than a plain bordered card. It is the
            only thing on the page a visitor might scan instead of read, so it gets the
            weight — a tinted ground, the section's accent on every tick, and the
            conversion button inside it rather than trailing underneath.
          */}
          <div
            className={cn(
              'rounded-[24px] border border-hairline-violet bg-gradient-to-b from-violet-50 to-white p-8',
              CARD_BASE_SHADOW,
            )}
          >
            <div className="mb-6 flex items-center gap-3">
              <div className="flex size-11 shrink-0 items-center justify-center rounded-[14px] bg-gradient-to-br from-violet-500 to-purple-600">
                <service.icon className="size-5 text-white" aria-hidden="true" />
              </div>
              <h2 className="text-[20px] font-bold text-heading">What is included</h2>
            </div>

            <ul className="space-y-4">
              {service.points.map((point) => (
                <li key={point} className="flex items-start gap-3">
                  <span
                    aria-hidden="true"
                    className="mt-0.5 flex size-[22px] shrink-0 items-center justify-center rounded-[6px] bg-violet-500"
                  >
                    <Check className="size-4 text-white" strokeWidth={3} />
                  </span>
                  <span className="text-[15px] leading-snug text-copy-strong">{point}</span>
                </li>
              ))}
            </ul>

            <ButtonLink to="/contact" className="mt-8 w-full justify-center">
              Discuss a brief
            </ButtonLink>
          </div>
        </Container>
      </section>
    </>
  );
}

// -----------------------------------------------------------------------------
// About
// -----------------------------------------------------------------------------

export function AboutSection({ className }: { className?: string } = {}): ReactNode {
  const textReveal = useReveal<HTMLDivElement>();
  const illustrationReveal = useReveal<HTMLDivElement>();

  return (
    <section className={cn('overflow-hidden bg-surface', sectionRhythm, className)}>
      {/*
        Not two equal columns. The prose runs to about 50 characters a line before it
        starts to read as a wall, and everything past that is better spent on the
        illustration, which is dense enough that a 576px render lost its labels.
      */}
      <Container
        pad="gutter"
        className="grid grid-cols-1 items-center gap-10 md:grid-cols-[minmax(0,0.85fr)_minmax(0,1.35fr)] lg:gap-12"
      >
        <div ref={textReveal} className="md:pr-8">
          <span className="mb-4 block text-[11px] font-bold uppercase tracking-widest text-primary">
            Why we exist
          </span>
          {/* The design's heading, kept — it is good, and it is not a product claim. */}
          <h2 className="mb-6 text-[40px] font-bold leading-[1.2] text-heading">
            Bridging the gap between potential and impact.
          </h2>
          <p className="mb-6 text-[16px] leading-relaxed text-body">
            The name comes from the idea that the right combination — exact skills, the right
            education, the relevant experience — is genuinely rare and genuinely hard to retrieve.
            Most of the work of recruitment is the retrieval.
          </p>
          <p className="text-[16px] leading-relaxed text-body">
            We are a boutique, which means you deal with the people doing the work. There is no
            account manager between you and the person running your search.
          </p>
        </div>

        {/*
          No `mix-blend-multiply` any more, and no `scale-125`. Both were there for the
          previous artwork, which sat in a wide white margin: the blend hid the margin and
          the scale clawed back the size it cost. This composition is full-bleed on a
          near-white ground that the section's own white already absorbs, so the blend is
          a no-op and the scale only overflows the column.
        */}
        <div ref={illustrationReveal} className="w-full xl:-mr-8">
          {/*
            Well below the fold, so `priority` is left off and the first illustration
            waits until it is nearly on screen like the rest.
          */}
          <RotatingArtwork
            names={ABOUT_IMAGE_NAMES}
            width={ABOUT_ILLUSTRATION.width}
            height={ABOUT_ILLUSTRATION.height}
          />
        </div>
      </Container>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Team
// -----------------------------------------------------------------------------

/**
 * The whole team, as a directory.
 *
 * Only `/team` renders it. The homepage and About both used to show a three-person preview;
 * once Team had its own menu entry, each preview was a second, worse door to a page the nav
 * already opens, and a screen of scrolling on a page that is about something else.
 *
 * No heading of its own: the `/team` hero directly above is the team heading, and with the
 * client's three-line heading, printing it twice cost a full screen before the first face.
 */
export function TeamSection({
  team,
  onSelectMember,
}: {
  team?: SettingValues['team'];
  /**
   * Makes each person's portrait, name and title one button, which hands their name back
   * to the page for the `TeamVoices` band underneath.
   *
   * Without it the entries stay plain: an affordance that scrolls to a section that is not
   * on the page would be a promise the page cannot keep.
   */
  onSelectMember?: (name: string) => void;
}): ReactNode {
  const { settings } = useSite();
  const value = team ?? settings.team;

  if (!value.enabled || value.members.length === 0) return null;

  const members = [...value.members].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <section className={cn('border-t border-gray-100 bg-surface', sectionRhythm)}>
      <Container pad="gutter">
        {/*
          A directory, not a wall of cards: a portrait, a name and a role, five to a row.

          The people used to sit in bordered cards four to a row. Each card spent 32px of
          padding a side and a border on framing one face, so ten people took three rows
          and the last held two cards and a hole. Without the frames the same width holds
          five, and the team reads as a team rather than as a list of separate boxes.

          Flex-wrap rather than a grid so a short last row is centred instead of hanging
          off the left edge. Each width is one column's share of the row less its share of
          the gaps (24px each), so full rows still fill edge to edge.

          Nothing closes the list: it is people only. The "Join us" invitation that used to
          end it is now `CtaBand`'s `joinUs` half, at the foot of the team page.
        */}
        <ul className="flex flex-wrap justify-center gap-x-6 gap-y-12 lg:gap-y-14">
          {members.map((member) => (
            <li
              key={member.name}
              className={cn(
                // A named group, so a hover anywhere on the person lifts the portrait
                // without colliding with the identity button's own `group`.
                'group/card flex flex-col items-center text-center',
                'w-[calc(50%-12px)] sm:w-[calc(33.333%-16px)] lg:w-[calc(20%-19.2px)]',
              )}
            >
              {/*
                The bio is deliberately *not* printed here. The same words carry the
                `TeamVoices` band underneath at a size somebody will actually read them at,
                and this stays what a directory entry should be: face, name, role.
              */}
              <MemberIdentity member={member} onSelect={onSelectMember} />

              <MemberLinks member={member} />
            </li>
          ))}
        </ul>
      </Container>
    </section>
  );
}

/**
 * One person as the settings store them.
 *
 * Derived from the schema rather than restated, so a field added in
 * `packages/shared/src/schemas/settings.ts` reaches this file without a second edit.
 */
type TeamMember = SettingValues['team']['members'][number];

/**
 * A team card's portrait, name and title — the whole of it one button when the page can
 * act on a selection.
 *
 * A button *inside* the card rather than a card that is itself a button: the card also
 * holds the LinkedIn and email links, and an anchor nested inside a button is invalid
 * markup that browsers resolve by dropping one of the two. This way the identity is one
 * hit target, the contact icons are their own, and clicking either does the thing its
 * appearance promises.
 *
 * When no handler is passed the same markup renders as plain elements. A button that
 * looks pressable and scrolls to a section that is not on the page would be worse than no
 * affordance at all.
 */
function MemberIdentity({
  member,
  onSelect,
}: {
  member: TeamMember;
  onSelect?: (name: string) => void;
}): ReactNode {
  /*
    With no card around it, the portrait carries the hover. On a hover anywhere on the
    person it lifts, its ring brightens and throws a violet glow, and the picture zooms
    *inside* the circle — so the face moves while the layout around it stays put. Keyboard
    focus on the identity button gets the same through the button's own group.
  */
  const zoom = cn(
    'size-full transition-transform duration-500 ease-out motion-reduce:transition-none',
    'group-hover/card:scale-110',
    onSelect ? 'group-focus-visible:scale-110' : null,
  );

  const portrait = (
    <div
      className={cn(
        'mx-auto mb-5 size-32 overflow-hidden rounded-full border-2 border-primary/15 lg:size-36',
        'transition-[translate,border-color,box-shadow] duration-300 ease-out motion-reduce:transition-none',
        'group-hover/card:-translate-y-1.5 group-hover/card:border-primary/60',
        'group-hover/card:shadow-[0_16px_32px_-10px_rgba(99,14,212,0.45),0_0_0_6px_rgba(124,58,237,0.12)]',
        onSelect
          ? 'group-focus-visible:-translate-y-1.5 group-focus-visible:border-primary/60'
          : null,
      )}
    >
      {member.photoMediaId ? (
        <img
          src={mediaUrl(member.photoMediaId, 'thumb')}
          alt={member.name}
          loading="lazy"
          className={cn(zoom, 'object-cover')}
        />
      ) : (
        // Initials placeholder. Better than a generic silhouette, and it
        // avoids shipping stock photography of people who do not work here.
        <span
          aria-hidden="true"
          className={cn(
            zoom,
            'grid place-items-center bg-gradient-to-br from-primary-strong to-primary text-3xl font-semibold text-white',
          )}
        >
          {initials(member.name)}
        </span>
      )}
    </div>
  );

  const name = (
    <h3 className="mb-1 text-[17px] font-semibold leading-snug text-title">{member.name}</h3>
  );
  const title = member.title ? (
    <p className="mb-4 text-[14px] font-medium leading-snug text-primary">{member.title}</p>
  ) : null;

  if (!onSelect) {
    return (
      <>
        {portrait}
        {name}
        {title}
      </>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onSelect(member.name)}
      /*
        The accessible name says what pressing it does. "Jaini Thakkar" alone would read as
        a link to a profile page, which is not what this is — there is no such page, and
        the button moves the reader to a section further down the one they are on.
      */
      aria-label={`Read ${member.name} in their own words`}
      className={cn(
        'group mb-0 flex w-full flex-col items-center rounded-[16px] focus-visible:outline',
        'focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary',
      )}
    >
      {portrait}
      {name}
      {title}
    </button>
  );
}

/** One round contact button, shared by the LinkedIn and email links so they cannot drift. */
const CONTACT_ICON_CLASS = cn(
  'grid size-9 place-items-center rounded-full border border-hairline bg-surface-raised text-copy',
  'transition-colors duration-200 hover:border-primary hover:bg-primary hover:text-white',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
);

/**
 * The row of contact icons under a team card.
 *
 * Extracted from the card so the LinkedIn address is normalised exactly once. The stored
 * value decides two things — whether the icon is rendered, and where it points — and when
 * those were two separate reads of `member.linkedin` in the JSX it was possible, and it
 * happened, for one to be right while the other was not. See `externalHref`: several rows
 * hold `linkedin.com/in/…` with no scheme, which an `href` resolves against the current
 * page rather than treating as an absolute address.
 */
function MemberLinks({ member }: { member: TeamMember }): ReactNode {
  const linkedinHref = externalHref(member.linkedin);

  /*
    Each icon only when there is somewhere for it to go. An icon with no address behind it
    is a button that does nothing — so a person with neither set gets no row at all, rather
    than an empty strip of padding at the foot of their card.
  */
  if (!linkedinHref && !member.email) return null;

  return (
    <div className="mt-auto flex justify-center gap-3">
      {linkedinHref ? (
        <a
          href={linkedinHref}
          target="_blank"
          rel="noopener noreferrer"
          className={CONTACT_ICON_CLASS}
          aria-label={`${member.name} on LinkedIn`}
        >
          <LinkedInIcon />
        </a>
      ) : null}
      {member.email ? (
        <a
          href={`mailto:${member.email}`}
          className={CONTACT_ICON_CLASS}
          aria-label={`Email ${member.name}`}
        >
          <Mail className="size-4" />
        </a>
      ) : null}
    </div>
  );
}

/**
 * The LinkedIn glyph.
 *
 * Inline rather than from `lucide-react`: that library dropped brand marks, and the
 * path here is the one the reference design shipped.
 */
function LinkedInIcon(): ReactNode {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="size-4 fill-current">
      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
    </svg>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

// -----------------------------------------------------------------------------
// Homepage contact block
// -----------------------------------------------------------------------------

/**
 * The homepage's closing contact block.
 *
 * The body is `ContactBlock`, exactly as /contact renders it, so the two are the same
 * design by construction. What was here before — a near-black split card with the details
 * on one side and the form on the other — was a second arrangement of the same content,
 * and the pair had drifted far enough apart to read as two different sites.
 *
 * The heading lives here rather than in the shared block because /contact already has one
 * in its `PageHero`; putting it inside would give that page two.
 */
export function ContactSection(): ReactNode {
  const { settings } = useSite();
  const reveal = useReveal<HTMLDivElement>();

  return (
    /*
      White, not the lavender wash the rest of the page alternates with, and that is the
      banner's doing: its ground is cut to transparent, so it needs a plain ground to
      dissolve into rather than a tint that would show through the antialiased edges.

      Extra padding below the shared rhythm, and only below: this is the last band before
      the footer, and running the form straight into a dark purple slab crowds it.
    */
    <section className={cn('relative bg-surface', sectionRhythm, 'pb-20 md:pb-24')}>
      <Container pad="gutter" className="relative z-10">
        <div ref={reveal}>
          <SectionHeading
            eyebrow="Contact"
            title="Let's talk about the role."
            description={
              settings.contact.responsePromise ||
              'Send us the brief and we will tell you honestly whether we are the right people for it.'
            }
            className="mb-10"
          />

          <ContactBlock sourcePath="/" idPrefix="home" />
        </div>
      </Container>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Closing call to action (used by the interior pages, not the homepage)
// -----------------------------------------------------------------------------

export function CtaBand({
  joinUs = false,
}: {
  /**
   * Adds a second half for recruiters who want to work here, beside the one for clients.
   *
   * Only `/team` sets it. That invitation used to be a "Join us" card at the end of the team
   * grid, where it was the one tile in a row of faces that was not a person, and it said
   * much the same thing as this band — so the two now share it. The client half stays
   * first and keeps the filled button: hiring is what the site is for, and a second button
   * of the same weight would make the two asks compete.
   */
  joinUs?: boolean;
} = {}): ReactNode {
  const { settings } = useSite();
  const careersLive = settings.featureFlags.showCareers;

  const hire = (
    <div className="flex flex-col items-center gap-6">
      <h2 className="max-w-2xl text-[1.75rem] font-semibold leading-tight text-white md:text-[2.25rem]">
        Tell us about the role you need to fill
      </h2>
      <p className="max-w-xl text-white/70">
        {settings.contact.responsePromise ||
          'Send us the brief and we will tell you honestly whether we are the right people for it.'}
      </p>
      <ButtonLink to="/contact" size="lg">
        Start a conversation
      </ButtonLink>
    </div>
  );

  return (
    <Section tone="surface" size="compact">
      <Container>
        <div className="relative overflow-hidden rounded-card bg-ink px-8 py-14 text-center md:px-16">
          <Glow className="left-1/2 top-0 size-[480px] -translate-x-1/2 opacity-30" />
          {joinUs ? (
            // Stacked with a rule between on small screens, side by side with a vertical
            // rule from `lg`. One card either way, so it reads as one closing section.
            <div className="relative grid items-center gap-12 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] lg:gap-0">
              <div className="lg:pr-14">{hire}</div>
              <div className="flex flex-col items-center gap-4 border-t border-white/15 pt-12 lg:border-l lg:border-t-0 lg:py-2 lg:pl-14 lg:pt-2">
                <Eyebrow className="text-secondary-container">
                  {careersLive ? 'Open roles' : 'Always interested'}
                </Eyebrow>
                <h2 className="text-[1.5rem] font-semibold leading-tight text-white">Join us</h2>
                <p className="max-w-sm text-[15px] leading-relaxed text-white/70">
                  We are always glad to hear from recruiters who work the way we do. Tell us what
                  you have been running and where you want to take it.
                </p>
                {/*
                  A same-tab router link, not `target="_blank"`: both destinations are pages
                  of this site, and a forced new tab takes the back button away. The careers
                  page is Phase 2, so while it is hidden this points at the contact form and
                  says so — an invitation that works rather than one that 404s.
                */}
                <Link
                  to={careersLive ? '/careers' : '/contact'}
                  className="group inline-flex items-center gap-1.5 py-2 text-[15px] font-semibold text-secondary-container transition-colors hover:text-white"
                >
                  {careersLive ? 'View careers' : 'Get in touch'}
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1 motion-reduce:transition-none" />
                </Link>
              </div>
            </div>
          ) : (
            <div className="relative">{hire}</div>
          )}
        </div>
      </Container>
    </Section>
  );
}
